import {useContext, useMemo} from "react";
import {Anchor, Text} from "@mantine/core";
import {GPTMetaContext} from "../MetaProvider";
import {
    artifactAwaitsOwnTitle,
    getArtifactTitle,
    getArtifactTypeLabel,
} from "../../artifacts/title";
import shimmer from "../../../shimmerText.module.css";

type ArtifactReferenceProps = {
    reference: string;
};

export const ArtifactReference = ({reference}: ArtifactReferenceProps) => {
    const meta = useContext(GPTMetaContext);

    const resolved = useMemo(() => {
        const match = reference.replace(/[[\]]/g, "").match(/^a(\d+)$/);
        if (!match) return null;

        const artifacts = meta?.artifacts;
        const index = parseInt(match[1], 10) - 1;
        if (!artifacts || index < 0 || index >= artifacts.length) {
            return {
                label: getArtifactTypeLabel(),
                artifactId: null,
                awaitsTitle: true,
            };
        }

        const artifact = artifacts[index];
        return {
            label: getArtifactTitle(artifact),
            artifactId: artifact.id,
            awaitsTitle: artifactAwaitsOwnTitle(artifact),
        };
    }, [reference, meta?.artifacts]);

    if (!resolved) {
        return <span>{reference}</span>;
    }

    const {label, artifactId, awaitsTitle} = resolved;
    const pending = awaitsTitle && !!meta?.isStreaming;

    if (pending || !artifactId) {
        return (
            <Text
                component="span"
                className={pending ? shimmer.shimmerText : undefined}
            >
                {label}
            </Text>
        );
    }

    return (
        <Anchor
            component="span"
            size="md"
            fw={500}
            style={{cursor: "pointer", overflowWrap: "anywhere"}}
            onClick={() => {
                document
                    .getElementById(`artifact-${artifactId}`)
                    ?.scrollIntoView({behavior: "smooth", block: "nearest"});
            }}
        >
            {label}
        </Anchor>
    );
};
